import { useEditorialMotion } from '@/hooks/useEditorialMotion'
import { lazy, Suspense, useEffect } from 'react'
import type { ReactNode } from 'react'
import { Routes, Route, Navigate, useParams, Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { defaultLanguage } from '@/i18n'
import { Header, Footer } from '@/components/layout'
const NotFound = lazy(async () => ({ default: (await import('@/pages/NotFound')).NotFound }))
const Home = lazy(async () => ({ default: (await import('@/pages/Home')).Home }))
const Technologies = lazy(async () => ({ default: (await import('@/pages/Technologies')).Technologies }))
const ProfessionalProducts = lazy(async () => ({ default: (await import('@/pages/ProfessionalProducts')).ProfessionalProducts }))
const Supplements = lazy(async () => ({ default: (await import('@/pages/Supplements')).Supplements }))
const Training = lazy(async () => ({ default: (await import('@/pages/Training')).Training }))
const Contacts = lazy(async () => ({ default: (await import('@/pages/Contacts')).Contacts }))
const ExclusiveDayPalermo = lazy(async () => ({ default: (await import('@/pages/ExclusiveDayPalermo')).ExclusiveDayPalermo }))
const ExclusiveDayCatania = lazy(async () => ({ default: (await import('@/pages/ExclusiveDayPalermo')).ExclusiveDayCatania }))
const Privacy = lazy(async () => ({ default: (await import('@/pages/Privacy')).Privacy }))
const Terms = lazy(async () => ({ default: (await import('@/pages/Terms')).Terms }))
const TechnologyPage = lazy(async () => ({ default: (await import('@/components/technologies/TechnologyPage')).TechnologyPage }))
import { mBodyL, mBodyS, mHydro5d, mLaserPro, mPress, mRfSynergy, mShock, mSynergyA } from '@/data/technologies'

function LanguagePage({ language, children }: { language: 'it' | 'en'; children: ReactNode }) {
  const { lang } = useParams()
  return lang === language ? children : <NotFound />
}

function RouteLoading() {
  const { t } = useTranslation('common')
  return <div className="route-loading" role="status" aria-live="polite"><span className="sr-only">{t('aria.loading')}</span></div>
}

function LanguageWrapper() {
  const { lang } = useParams()
  const { t, i18n } = useTranslation('common')
  const location = useLocation()
  useEditorialMotion(location.pathname)
  useEffect(() => {
    const language = lang === 'en' ? 'en' : 'it'
    document.documentElement.lang = language
    // Run after all translation subscribers have mounted, including cached fallback resources.
    if (i18n.resolvedLanguage !== language) {
      const timer = window.setTimeout(() => { void i18n.changeLanguage(language) }, 0)
      return () => window.clearTimeout(timer)
    }
  }, [lang, i18n, i18n.resolvedLanguage])
  useEffect(() => {
    if (location.state?.preserveScroll) return
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    const frame = requestAnimationFrame(() => {
      if (location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior })
      else window.scrollTo({ top: 0, left: 0, behavior })
    })
    return () => cancelAnimationFrame(frame)
  }, [location.pathname, location.hash])
  return <div className="medika-site"><a className="skip-link" href="#main-content">{t('shell.skip')}</a><Header /><main id="main-content" tabIndex={-1}>{lang === 'it' || lang === 'en' ? <Outlet /> : <NotFound />}</main><Footer /></div>
}
export function LanguageRouter() {
  return <Suspense fallback={<RouteLoading />}><Routes><Route path="/" element={<Navigate to={`/${defaultLanguage}`} replace />} />
    <Route path="/:lang" element={<LanguageWrapper />}>
      <Route index element={<Home />} />
      <Route path="tecnologie" element={<LanguagePage language="it"><Technologies /></LanguagePage>} />
      <Route path="technologies" element={<LanguagePage language="en"><Technologies /></LanguagePage>} />
      <Route path="prodotti-da-cabina" element={<LanguagePage language="it"><ProfessionalProducts /></LanguagePage>} />
      <Route path="professional-products" element={<LanguagePage language="en"><ProfessionalProducts /></LanguagePage>} />
      <Route path="integratori" element={<LanguagePage language="it"><Supplements /></LanguagePage>} />
      <Route path="supplements" element={<LanguagePage language="en"><Supplements /></LanguagePage>} />
      <Route path="formazione" element={<LanguagePage language="it"><Training /></LanguagePage>} />
      <Route path="training" element={<LanguagePage language="en"><Training /></LanguagePage>} />
      <Route path="contatti" element={<LanguagePage language="it"><Contacts /></LanguagePage>} />
      <Route path="contacts" element={<LanguagePage language="en"><Contacts /></LanguagePage>} />
      <Route path="privacy" element={<Privacy />} />
      <Route path="termini-e-condizioni" element={<LanguagePage language="it"><Terms /></LanguagePage>} />
      <Route path="terms-and-conditions" element={<LanguagePage language="en"><Terms /></LanguagePage>} />
      <Route path="evento-exclusive-day-palermo" element={<LanguagePage language="it"><ExclusiveDayPalermo /></LanguagePage>} />
      <Route path="exclusive-day-palermo" element={<LanguagePage language="en"><ExclusiveDayPalermo /></LanguagePage>} />
      <Route path="evento-exclusive-day-catania" element={<LanguagePage language="it"><ExclusiveDayCatania /></LanguagePage>} />
      <Route path="exclusive-day-catania" element={<LanguagePage language="en"><ExclusiveDayCatania /></LanguagePage>} />
      <Route path="tecnologie/m-hydro-5d" element={<LanguagePage language="it"><TechnologyPage technology={mHydro5d} /></LanguagePage>} />
      <Route path="technologies/m-hydro-5d" element={<LanguagePage language="en"><TechnologyPage technology={mHydro5d} /></LanguagePage>} />
      <Route path="tecnologie/m-laser-pro" element={<LanguagePage language="it"><TechnologyPage technology={mLaserPro} /></LanguagePage>} />
      <Route path="technologies/m-laser-pro" element={<LanguagePage language="en"><TechnologyPage technology={mLaserPro} /></LanguagePage>} />
      <Route path="tecnologie/m-body-l" element={<LanguagePage language="it"><TechnologyPage technology={mBodyL} /></LanguagePage>} />
      <Route path="technologies/m-body-l" element={<LanguagePage language="en"><TechnologyPage technology={mBodyL} /></LanguagePage>} />
      <Route path="tecnologie/m-body-s" element={<LanguagePage language="it"><TechnologyPage technology={mBodyS} /></LanguagePage>} />
      <Route path="technologies/m-body-s" element={<LanguagePage language="en"><TechnologyPage technology={mBodyS} /></LanguagePage>} />
      <Route path="tecnologie/m-press" element={<LanguagePage language="it"><TechnologyPage technology={mPress} /></LanguagePage>} />
      <Route path="technologies/m-press" element={<LanguagePage language="en"><TechnologyPage technology={mPress} /></LanguagePage>} />
      <Route path="tecnologie/m-shock" element={<LanguagePage language="it"><TechnologyPage technology={mShock} /></LanguagePage>} />
      <Route path="technologies/m-shock" element={<LanguagePage language="en"><TechnologyPage technology={mShock} /></LanguagePage>} />
      <Route path="tecnologie/m-rf-synergy" element={<LanguagePage language="it"><TechnologyPage technology={mRfSynergy} /></LanguagePage>} />
      <Route path="technologies/m-rf-synergy" element={<LanguagePage language="en"><TechnologyPage technology={mRfSynergy} /></LanguagePage>} />
      <Route path="tecnologie/m-synergy-a" element={<LanguagePage language="it"><TechnologyPage technology={mSynergyA} /></LanguagePage>} />
      <Route path="technologies/m-synergy-a" element={<LanguagePage language="en"><TechnologyPage technology={mSynergyA} /></LanguagePage>} />
      <Route path="*" element={<NotFound />} />
    </Route>
  </Routes></Suspense>
}
